// hooks/usePaginatedData.ts
import { useEffect, useRef, useState } from 'react';

export interface PaginatedResponse<T> {
  count: number;
  results: T[];
  next: string | null;
  previous: string | null;
}

export function usePaginatedData<T extends { id: number | string }>(
  fetcher: (page: number, pageSize: number) => Promise<PaginatedResponse<T>>,
  deps: any[] = [],
  appendMode = false
) {
  const [data, setData] = useState<T[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Reset state when dependencies change
  useEffect(() => {
    setData([]);
    setPage(0);
    setHasMore(true);
    setInitialLoadDone(false);
    console.log("deps changed: ", deps)
  }, deps);

  useEffect(() => {
    let cancelled = false;

    // Don't fetch if we know there's no more data
    if (!hasMore && page > 0) {
      return;
    }

    setLoading(true);

    fetcher(page, rowsPerPage).then((res) => {
      //if (cancelled) return;

      console.log("received the following data for page ", page, " => ", res)

      if (appendMode) {
        // Append mode: add new results to existing data
        setData((prev) => [...prev, ...res.results]);
      } else {
        // Normal mode: replace data
        setData(res.results);
      }

      setCount(res.count);
      setHasMore(res.next != null);
      setInitialLoadDone(true);
    }).catch((error) => {
        console.error(error)
      if (cancelled) return;
      // If we get an error (like 404), assume no more pages
      setHasMore(false);
    }).finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage, ...deps]);

  const loadNextPage = () => {
    if (!loading && hasMore && initialLoadDone) {
      setPage((prev) => prev + 1);
    }
  };

  const setCurrentPage = (newPage: number) => {
    setPage(newPage);
  };

  const prependRecord = (record: T) => {
    if (page > 0) return;

    if (data.length == rowsPerPage) {
      setData([record, ...data.slice(0, rowsPerPage - 1)]);
    } else {
      setData([record, ...data]);
      setCount(count + 1);
    }
  }

  const updateRecord = (record: T) => {
    setData(data.map(r => r.id === record.id ? record : r));
  }

  return {
    data,
    count,
    page: page, // Return 1-based page for display
    rowsPerPage,
    loading,
    hasMore,
    setPage: setCurrentPage,
    setRowsPerPage,
    loadNextPage,
    prependRecord,
    updateRecord,
    refetch: () => {
      // For refetch, go back to first page
      setPage(0);
      setHasMore(true);
    },
  };
}